import React from "react";
import {
  AlertCircle,
  Boxes,
  ChevronLeft,
  ChevronRight,
  Layers,
  Pencil,
  Server,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import type {
  ComponentControlRow,
  InventoryItemResponse,
  SspComponentResponse,
} from "../../../../api/frameworksApi";
import { Dropdown } from "../../../components/Dropdown";
import {
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
} from "../../../components/Modal";
import { Tip } from "../../../components/ui/tooltip";
import {
  COMP_DETAIL_PAGE_SIZE_OPTIONS,
  COMPONENT_TYPES,
  SSP_LAYERS,
  SYSTEM_STATUSES,
  type SspLayerKey,
} from "../constants";
import { LoadingSpinner } from "../components/LoadingSpinner";
import {
  getComponentLayer,
  getControlCountLabel,
  getDisplayStatusStyle,
} from "../utils/components";

export interface ComponentsTabProps {
  // Detail selection
  selectedComponentUuid: string | null;
  setSelectedComponentUuid: React.Dispatch<React.SetStateAction<string | null>>;
  sspComponents: SspComponentResponse[];

  // Detail pagination
  compInvPage: number;
  setCompInvPage: React.Dispatch<React.SetStateAction<number>>;
  compCtrlPage: number;
  setCompCtrlPage: React.Dispatch<React.SetStateAction<number>>;
  compPageSize: number;
  setCompPageSize: React.Dispatch<React.SetStateAction<number>>;

  // Detail data
  compInventory: InventoryItemResponse[];
  compControls: ComponentControlRow[];
  compDetailLoading: boolean;
  compDetailTab: "inventory" | "controls";
  setCompDetailTab: React.Dispatch<
    React.SetStateAction<"inventory" | "controls">
  >;
  handleOpenComponentDetail: (componentId: string) => Promise<void> | void;

  // List state
  componentsLoading: boolean;
  componentDeleteError: string | null;
  setComponentDeleteError: React.Dispatch<React.SetStateAction<string | null>>;
  componentGroups: Record<string, SspComponentResponse[]>;
  availableComponentTypes: string[];
  componentTypeFilter: string;
  setComponentTypeFilter: (v: string) => void;
  availableGroupNames: string[];
  componentGroupFilter: string;
  setComponentGroupFilter: (v: string) => void;
  componentSearchFilter: string;
  setComponentSearchFilter: (v: string) => void;

  // Layer view
  selectedLayerKey: SspLayerKey;
  setSelectedLayerKey: React.Dispatch<React.SetStateAction<SspLayerKey>>;
  isDark: boolean;

  // Edit form
  editingComponentId: string | null;
  setEditingComponentId: React.Dispatch<React.SetStateAction<string | null>>;
  editComponentForm: {
    title: string;
    componentType: string;
    description: string;
    purpose: string;
    status: string;
    statusRemarks: string;
  };
  setEditComponentForm: React.Dispatch<
    React.SetStateAction<{
      title: string;
      componentType: string;
      description: string;
      purpose: string;
      status: string;
      statusRemarks: string;
    }>
  >;
  savingComponent: boolean;
  handleUpdateComponent: () => void;

  // Delete
  handleDeleteComponent: (componentUuid: string) => Promise<void>;
  deletingComponentId: string | null;

  // Permissions / helpers
  isPublishedSsp: boolean;
  requestDelete: (
    type: string,
    id: string,
    title: string,
    action: () => Promise<void>,
  ) => void;
}

export function ComponentsTab({
  selectedComponentUuid,
  setSelectedComponentUuid,
  sspComponents,
  compInvPage,
  setCompInvPage,
  compCtrlPage,
  setCompCtrlPage,
  compPageSize,
  setCompPageSize,
  compInventory,
  compControls,
  compDetailLoading,
  compDetailTab,
  setCompDetailTab,
  handleOpenComponentDetail,
  componentsLoading,
  componentDeleteError,
  setComponentDeleteError,
  componentGroups,
  availableComponentTypes,
  componentTypeFilter,
  setComponentTypeFilter,
  availableGroupNames,
  componentGroupFilter,
  setComponentGroupFilter,
  componentSearchFilter,
  setComponentSearchFilter,
  selectedLayerKey,
  setSelectedLayerKey,
  isDark,
  editingComponentId,
  setEditingComponentId,
  editComponentForm,
  setEditComponentForm,
  savingComponent,
  handleUpdateComponent,
  handleDeleteComponent,
  deletingComponentId,
  isPublishedSsp,
  requestDelete,
}: ComponentsTabProps) {
  return (
    <>
      {/* ── Component Detail View ── */}
      {selectedComponentUuid ? (
        (() => {
          const comp = sspComponents.find((c) => c.componentUuid === selectedComponentUuid || c.id === selectedComponentUuid);
          // ── Assets pagination (client-side) ──
          const invStart = compInvPage * compPageSize;
          const shownInventory = compInventory.slice(
            invStart,
            invStart + compPageSize,
          );
          const invTotal = compInventory.length;
          const invHasNext =
            (compInvPage + 1) * compPageSize < compInventory.length;
          // ── Controls pagination (client-side) ──
          const ctrlStart = compCtrlPage * compPageSize;
          const shownControls = compControls.slice(
            ctrlStart,
            ctrlStart + compPageSize,
          );
          const ctrlTotal = compControls.length;
          const ctrlHasNext =
            (compCtrlPage + 1) * compPageSize < ctrlTotal;
          return (
            <div className="space-y-4">
              {/* Back button + header */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { setSelectedComponentUuid(null); setEditingComponentId(null); }}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ChevronLeft className="size-3.5" /> Back to components
                </button>
              </div>
              {comp && (
                <div className="rounded-lg border border-border bg-card p-4">
                  <div className="flex items-start gap-3">
                    <div className="size-9 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                      <Server className="size-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">{comp.title}</p>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-muted text-muted-foreground capitalize">{comp.componentType}</span>
                        {(comp.displayStatusLabel || comp.displayStatus) && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${getDisplayStatusStyle(comp.displayStatus)}`}>
                            {comp.displayStatusLabel || comp.displayStatus}
                          </span>
                        )}
                      </div>
                      {comp.description && <p className="text-xs text-muted-foreground mt-1">{comp.description}</p>}
                    </div>
                  </div>
                </div>
              )}
              {/* Sub-tabs */}
              <div className="flex items-center gap-1 border-b border-border">
                {(["inventory", "controls"] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setCompDetailTab(t)}
                    className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors capitalize ${compDetailTab === t ? "border-indigo-500 text-indigo-600 dark:text-indigo-400" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                  >
                    {t === "inventory" ? `Assets${comp?.assetCount != null ? ` (${comp.assetCount})` : ""}` : `Controls${comp?.implementedControlCount != null ? ` (${comp.implementedControlCount})` : ""}`}
                  </button>
                ))}
              </div>
              {compDetailLoading ? (
                <LoadingSpinner text="Loading" />
              ) : compDetailTab === "inventory" ? (
                compInventory.length > 0 ? (
                  <div className="rounded-lg border border-border bg-card flex flex-col h-[460px] overflow-hidden">
                    {/* Header: title + rows per page (top-right) */}
                    <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border flex-shrink-0">
                      <span className="text-xs font-semibold text-foreground">Assets</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-muted-foreground">Rows per page</span>
                        <Dropdown
                          value={String(compPageSize)}
                          onChange={(v) => { setCompPageSize(Number(v)); setCompInvPage(0); setCompCtrlPage(0); }}
                          options={COMP_DETAIL_PAGE_SIZE_OPTIONS}
                          variant="pill"
                        />
                      </div>
                    </div>
                    {/* Scrollable body */}
                    <div className="flex-1 overflow-y-auto divide-y divide-border">
                      {shownInventory.map((item) => (
                        <div key={item.id} className="flex items-start gap-3 px-4 py-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{item.description}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              {item.assetIdentifiers.map((ai) => (
                                <span key={ai.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted text-[10px] font-mono">
                                  <span className="text-muted-foreground">{ai.identifierType}:</span> {ai.identifierValue}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Footer: showing + pager */}
                    <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-2.5 border-t border-border flex-shrink-0">
                      <p className="text-xs text-muted-foreground">
                        Showing{" "}
                        <span className="font-semibold text-[#334155] dark:text-white/90">
                          {invStart + 1}–{invStart + shownInventory.length}
                        </span>{" "}
                        of{" "}
                        <span className="font-semibold text-[#334155] dark:text-white/90">
                          {invTotal}
                        </span>
                      </p>
                      {(compInvPage > 0 || invHasNext) && (
                        <div className="flex items-center gap-2">
                          <button
                            disabled={compInvPage === 0}
                            onClick={() => setCompInvPage((p) => Math.max(0, p - 1))}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-xs text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronLeft className="size-3" /> Prev
                          </button>
                          <span className="text-xs text-muted-foreground">
                            Page {compInvPage + 1}
                          </span>
                          <button
                            disabled={!invHasNext}
                            onClick={() => setCompInvPage((p) => p + 1)}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-xs text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            Next <ChevronRight className="size-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No assets linked to this component.</p>
                )
              ) : (
                compControls.length > 0 ? (
                  <div className="rounded-lg border border-border bg-card flex flex-col h-[460px] overflow-hidden">
                    {/* Header: title + rows per page (top-right) */}
                    <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border flex-shrink-0">
                      <span className="text-xs font-semibold text-foreground">Controls</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-muted-foreground">Rows per page</span>
                        <Dropdown
                          value={String(compPageSize)}
                          onChange={(v) => { setCompPageSize(Number(v)); setCompInvPage(0); setCompCtrlPage(0); }}
                          options={COMP_DETAIL_PAGE_SIZE_OPTIONS}
                          variant="pill"
                        />
                      </div>
                    </div>
                    {/* Column headers */}
                    <div className="grid grid-cols-[90px_minmax(0,1fr)_minmax(0,2fr)_150px] gap-4 px-4 py-2 bg-muted/40 border-b border-border text-[10px] font-bold tracking-wider text-muted-foreground flex-shrink-0">
                      <span>Control ID</span>
                      <span>Control Title</span>
                      <span>Statement</span>
                      <span className="text-center">Implementation Status</span>
                    </div>
                    {/* Scrollable body */}
                    <div className="flex-1 overflow-y-auto divide-y divide-border">
                      {shownControls.map((ctrl) => {
                        const controlTitle = ctrl.controlTitle ?? ctrl.title ?? null;
                        const s = (ctrl.implementationStatus || "").toLowerCase();
                        const statusStyle =
                          s === "implemented"
                            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                            : s === "partial"
                              ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400"
                              : s === "planned"
                                ? "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400"
                                : s === "alternative"
                                  ? "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400"
                                  : s === "not-implemented"
                                    ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400"
                                    : "bg-muted text-muted-foreground";
                        return (
                          <div key={ctrl.implementedRequirementId} className="grid grid-cols-[90px_minmax(0,1fr)_minmax(0,2fr)_150px] gap-4 items-center px-4 py-3">
                            <span className="text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400 truncate">{ctrl.controlId?.toUpperCase()}</span>
                            <p className="text-xs font-semibold text-foreground truncate" title={controlTitle || ""}>{controlTitle || "—"}</p>
                            <p className="text-[11px] text-muted-foreground truncate" title={ctrl.statement || ctrl.description || ""}>{ctrl.statement || ctrl.description || "—"}</p>
                            <div className="flex justify-center">
                              {ctrl.implementationStatus ? (
                                <span className={`inline-flex items-center justify-center w-[110px] px-3 py-0.5 rounded-full text-[10px] font-bold ${statusStyle}`}>
                                  {ctrl.implementationStatus.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-[10px]">—</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Footer: showing + pager */}
                    <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-2.5 border-t border-border flex-shrink-0">
                      <p className="text-xs text-muted-foreground">
                        Showing{" "}
                        <span className="font-semibold text-[#334155] dark:text-white/90">
                          {ctrlStart + 1}–{ctrlStart + shownControls.length}
                        </span>{" "}
                        of{" "}
                        <span className="font-semibold text-[#334155] dark:text-white/90">
                          {ctrlTotal}
                        </span>
                      </p>
                      {(compCtrlPage > 0 || ctrlHasNext) && (
                        <div className="flex items-center gap-2">
                          <button
                            disabled={compCtrlPage === 0}
                            onClick={() => setCompCtrlPage((p) => Math.max(0, p - 1))}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-xs text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronLeft className="size-3" /> Prev
                          </button>
                          <span className="text-xs text-muted-foreground">
                            Page {compCtrlPage + 1}
                          </span>
                          <button
                            disabled={!ctrlHasNext}
                            onClick={() => setCompCtrlPage((p) => p + 1)}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-xs text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            Next <ChevronRight className="size-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No controls implemented by this component.</p>
                )
              )}
            </div>
          );
        })()
      ) : componentsLoading ? (
        <LoadingSpinner text="Loading components" />
      ) : sspComponents.length > 0 ? (
        <div className="space-y-4">
          {/* Component delete error banner */}
          {componentDeleteError && (
            <div className="bg-red-50 dark:bg-red-500/10 border border-red-300 dark:border-red-500/30 text-red-800 dark:text-red-300 rounded-md p-3 text-sm flex items-start gap-2">
              <AlertCircle className="size-4 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                {componentDeleteError}
                <button onClick={() => setComponentDeleteError(null)} className="ml-2 text-xs underline">Dismiss</button>
              </div>
            </div>
          )}

          {/* Filter bar */}
          <div className="flex items-center gap-3 flex-wrap">
            {availableComponentTypes.length > 1 && (
              <Dropdown
                value={componentTypeFilter}
                onChange={(v) => setComponentTypeFilter(v)}
                options={[{ value: "", label: "All Types" }, ...availableComponentTypes.map((t) => ({ value: t, label: t }))]}
                variant="pill"
              />
            )}
            {availableGroupNames.length > 1 && (
              <Dropdown
                value={componentGroupFilter}
                onChange={(v) => setComponentGroupFilter(v)}
                options={[{ value: "", label: "All Groups" }, ...availableGroupNames.map((g) => ({ value: g, label: g }))]}
                variant="pill"
              />
            )}
            {(componentSearchFilter || componentTypeFilter || componentGroupFilter) && (
              <button
                onClick={() => { setComponentSearchFilter(""); setComponentTypeFilter(""); setComponentGroupFilter(""); }}
                className="px-2.5 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>

          {/* ── Cylindrical SSP Layer View ── */}
          {(() => {
            // Flatten currently-filtered components from componentGroups
            const filteredComps = Object.values(componentGroups).flat();
            // Bucket by SSP layer
            const layerBuckets: Record<SspLayerKey, typeof filteredComps> = {
              application: [],
              data: [],
              runtime: [],
              compute: [],
              storage: [],
              platform: [],
              network: [],
            };
            for (const comp of filteredComps) {
              layerBuckets[getComponentLayer(comp)].push(comp);
            }
            const totalComponents = filteredComps.length;
            const layersWithItems = SSP_LAYERS.filter(
              (l) => layerBuckets[l.key].length > 0,
            ).length;
            const coveragePct =
              SSP_LAYERS.length > 0
                ? Math.round((layersWithItems / SSP_LAYERS.length) * 100)
                : 0;
            const activeLayer =
              SSP_LAYERS.find((l) => l.key === selectedLayerKey) ??
              SSP_LAYERS[0];
            const layerComps = layerBuckets[activeLayer.key];

            if (totalComponents === 0) {
              return (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No components match the current filters.
                </p>
              );
            }

            return (
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800/60 bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-[#0c1126] dark:to-slate-950 p-4 sm:p-6 text-slate-900 dark:text-white shadow-xl overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)_minmax(0,1.05fr)] gap-5">
                  {/* ── Component Overview ── */}
                  <div className="rounded-xl bg-slate-100/70 dark:bg-white/[0.03] border border-slate-200 dark:border-white/[0.08] p-4 flex flex-col gap-3">
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                        Component Overview
                      </h4>
                      <p className="text-[11px] text-slate-600 dark:text-white/60 leading-relaxed mt-1">
                        All components organized across {SSP_LAYERS.length}{" "}
                        security plan layers
                      </p>
                    </div>
                    <div className="grid gap-2">
                      <div className="flex items-center gap-3 rounded-lg bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.06] px-3 py-2.5">
                        <div className="size-9 rounded-md bg-indigo-100 dark:bg-indigo-500/15 border border-indigo-300 dark:border-indigo-400/30 flex items-center justify-center flex-shrink-0">
                          <Boxes className="size-4 text-indigo-600 dark:text-indigo-300" />
                        </div>
                        <div>
                          <p className="text-base font-bold leading-none">
                            {totalComponents}
                          </p>
                          <p className="text-[10px] tracking-wider text-slate-500 dark:text-white/60 mt-1">
                            Total Components
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 rounded-lg bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.06] px-3 py-2.5">
                        <div className="size-9 rounded-md bg-emerald-100 dark:bg-emerald-500/15 border border-emerald-300 dark:border-emerald-400/30 flex items-center justify-center flex-shrink-0">
                          <Layers className="size-4 text-emerald-600 dark:text-emerald-300" />
                        </div>
                        <div>
                          <p className="text-base font-bold leading-none">
                            {SSP_LAYERS.length}
                          </p>
                          <p className="text-[10px] tracking-wider text-slate-500 dark:text-white/60 mt-1">
                            SSP Layers
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 rounded-lg bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.06] px-3 py-2.5">
                        <div className="size-9 rounded-md bg-violet-100 dark:bg-violet-500/15 border border-violet-300 dark:border-violet-400/30 flex items-center justify-center flex-shrink-0">
                          <ShieldCheck className="size-4 text-violet-600 dark:text-violet-300" />
                        </div>
                        <div>
                          <p className="text-base font-bold leading-none">
                            {coveragePct}%
                          </p>
                          <p className="text-[10px] tracking-wider text-slate-500 dark:text-white/60 mt-1">
                            Coverage
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── SSP Layer Rows (flat cards with subtle gradient tint) ── */}
                  <div className="relative flex flex-col justify-center py-2 mx-auto w-full">
                    <div className="relative w-full flex flex-col gap-2.5">
                      {SSP_LAYERS.map((layer) => {
                        const count = layerBuckets[layer.key].length;
                        const active = selectedLayerKey === layer.key;
                        const Icon = layer.icon;
                        const [, mid] = layer.colors;
                        return (
                          <button
                            key={layer.key}
                            onClick={() =>
                              setSelectedLayerKey(layer.key)
                            }
                            className="group relative block w-full rounded-2xl border transition-all duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent overflow-hidden"
                            style={{
                              background: isDark
                                ? `linear-gradient(90deg, ${mid}26 0%, ${mid}10 50%, ${mid}22 100%)`
                                : `linear-gradient(90deg, ${mid}18 0%, ${mid}08 50%, ${mid}14 100%)`,
                              borderColor: active
                                ? mid
                                : isDark
                                  ? `${mid}45`
                                  : `${mid}35`,
                              boxShadow: active
                                ? isDark
                                  ? `0 10px 28px -10px ${mid}80, 0 0 0 1px ${mid}66 inset`
                                  : `0 10px 24px -10px ${mid}60, 0 0 0 1px ${mid}55 inset`
                                : undefined,
                              transform: active
                                ? "translateX(4px) scale(1.01)"
                                : undefined,
                            }}
                          >
                            {/* Left accent bar — expands on hover/active (transition only during hover, so leaving snaps back) */}
                            <span
                              aria-hidden
                              className={`pointer-events-none absolute left-0 top-0 h-full rounded-l-2xl group-hover:transition-all group-hover:duration-300 group-hover:ease-out ${active
                                ? "w-1"
                                : "w-0 group-hover:w-1"
                                }`}
                              style={{ background: mid }}
                            />
                            {/* Sheen sweep on hover */}
                            <span
                              aria-hidden
                              className="pointer-events-none absolute inset-y-0 left-0 w-1/3 -translate-x-full skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:translate-x-[320%] group-hover:transition-transform group-hover:duration-[900ms] group-hover:ease-out"
                            />
                            <div className="relative flex items-center justify-between gap-3 px-4 py-3.5">
                              <div className="flex items-center gap-3 min-w-0">
                                <div
                                  className="size-10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:transition-transform group-hover:duration-300 group-hover:ease-out group-hover:scale-110"
                                  style={{
                                    background: isDark
                                      ? `${mid}2a`
                                      : `${mid}1c`,
                                    border: `1px solid ${mid}55`,
                                  }}
                                >
                                  <Icon
                                    className="size-5"
                                    style={{ color: mid }}
                                  />
                                </div>
                                <span
                                  className={`text-[14px] font-bold tracking-tight truncate ${isDark
                                    ? "text-white"
                                    : "text-slate-800"
                                    }`}
                                >
                                  {layer.label}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span
                                  className="inline-flex items-center justify-center min-w-[30px] h-7 px-2.5 rounded-full text-[13px] font-bold tabular-nums group-hover:transition-transform group-hover:duration-300 group-hover:ease-out group-hover:scale-110"
                                  style={{
                                    background: isDark
                                      ? `${mid}22`
                                      : `${mid}18`,
                                    color: mid,
                                    border: `1px solid ${mid}40`,
                                  }}
                                >
                                  {count}
                                </span>
                                <ChevronRight
                                  className={`size-4 group-hover:transition-transform group-hover:duration-300 group-hover:ease-out ${active
                                    ? "translate-x-1"
                                    : "group-hover:translate-x-1"
                                    }`}
                                  style={{
                                    color: isDark
                                      ? "rgba(255,255,255,0.65)"
                                      : "rgba(71,85,105,0.75)",
                                  }}
                                />
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── Selected Layer Components ── */}
                  <div className="rounded-xl bg-slate-100/70 dark:bg-white/[0.03] border border-slate-200 dark:border-white/[0.08] p-4 flex flex-col">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className={`size-2 rounded-full ${activeLayer.dot}`}
                        />
                        <h4 className="text-sm font-bold text-slate-900 dark:text-white truncate">
                          Components in {activeLayer.label}
                        </h4>
                      </div>
                      <span className="text-[10px] font-bold tracking-wider text-slate-500 dark:text-white/60">
                        {layerComps.length}{" "}
                        {layerComps.length === 1
                          ? "Component"
                          : "Components"}
                      </span>
                    </div>
                    {layerComps.length === 0 ? (
                      <div className="flex-1 flex flex-col items-center justify-center text-center py-10 px-3">
                        <Server className="size-7 text-slate-400 dark:text-white/30 mb-2" />
                        <p className="text-sm font-medium text-slate-700 dark:text-white/80">
                          No components in this layer
                        </p>
                        <p className="text-[11px] text-slate-500 dark:text-white/50 mt-1">
                          Add components and tag them with this layer
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
                        {layerComps.map((comp) =>
                          editingComponentId === comp.id ? (
                            <div
                              key={comp.id}
                              className="rounded-lg bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.1] p-3 space-y-3 text-foreground"
                            >
                              <div className="grid grid-cols-2 gap-3">
                                <FormField label="Title">
                                  <FormInput
                                    value={editComponentForm.title}
                                    onChange={(v) =>
                                      setEditComponentForm((f) => ({
                                        ...f,
                                        title: v,
                                      }))
                                    }
                                    placeholder="Component title"
                                  />
                                </FormField>
                                <FormField label="Type">
                                  <FormSelect
                                    options={COMPONENT_TYPES}
                                    value={editComponentForm.componentType}
                                    onChange={(v) =>
                                      setEditComponentForm((f) => ({
                                        ...f,
                                        componentType: v,
                                      }))
                                    }
                                    placeholder="Select type"
                                  />
                                </FormField>
                              </div>
                              <FormField label="Description">
                                <FormTextarea
                                  value={editComponentForm.description}
                                  onChange={(v) =>
                                    setEditComponentForm((f) => ({
                                      ...f,
                                      description: v,
                                    }))
                                  }
                                  placeholder="Description"
                                />
                              </FormField>
                              <div className="grid grid-cols-2 gap-3">
                                <FormField label="Purpose">
                                  <FormInput
                                    value={editComponentForm.purpose}
                                    onChange={(v) =>
                                      setEditComponentForm((f) => ({
                                        ...f,
                                        purpose: v,
                                      }))
                                    }
                                    placeholder="Purpose"
                                  />
                                </FormField>
                                <FormField label="Status">
                                  <FormSelect
                                    options={SYSTEM_STATUSES}
                                    value={editComponentForm.status}
                                    onChange={(v) =>
                                      setEditComponentForm((f) => ({
                                        ...f,
                                        status: v,
                                      }))
                                    }
                                    placeholder="Select status"
                                  />
                                </FormField>
                              </div>
                              <div className="flex items-center gap-2 pt-1">
                                <button
                                  onClick={() =>
                                    setEditingComponentId(null)
                                  }
                                  className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-white/15 text-xs font-medium text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-white/[0.06] transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleUpdateComponent}
                                  disabled={
                                    !editComponentForm.title ||
                                    savingComponent
                                  }
                                  className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50"
                                >
                                  {savingComponent ? "Saving" : "Save"}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div
                              key={comp.id}
                              className="group rounded-lg bg-white dark:bg-white/[0.04] hover:bg-slate-50 dark:hover:bg-white/[0.07] border border-slate-200 dark:border-white/[0.08] hover:border-slate-300 dark:hover:border-white/[0.18] p-3 transition-all"
                            >
                              <div className="flex items-start gap-3">
                                <div
                                  className={`size-9 rounded-md bg-gradient-to-br ${activeLayer.gradient} flex items-center justify-center flex-shrink-0 shadow-inner`}
                                >
                                  <Server className="size-4 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <button
                                    onClick={() =>
                                      handleOpenComponentDetail(comp.id)
                                    }
                                    className="text-sm font-semibold text-slate-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors text-left truncate w-full"
                                  >
                                    {comp.title}
                                  </button>
                                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/[0.08] text-slate-700 dark:text-white/80 capitalize">
                                      {comp.componentType}
                                    </span>
                                    {(comp.implementedControlCount ?? 0) > 0 &&
                                      (comp.displayStatusLabel || comp.displayStatus) && (
                                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${getDisplayStatusStyle(comp.displayStatus)}`}>
                                          {comp.displayStatusLabel || comp.displayStatus}
                                        </span>
                                      )}
                                  </div>
                                </div>
                                {!isPublishedSsp && (
                                  <div className="flex items-center gap-1 flex-shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
                                    <Tip label="Edit Component" side="bottom">
                                      <button
                                        onClick={() => {
                                          setEditingComponentId(comp.id);
                                          setEditComponentForm({
                                            title: comp.title,
                                            componentType:
                                              comp.componentType || "",
                                            description:
                                              comp.description || "",
                                            purpose: comp.purpose || "",
                                            status: comp.status || "",
                                            statusRemarks:
                                              comp.statusRemarks || "",
                                          });
                                        }}
                                        className="p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-slate-500 dark:text-white/70 hover:text-indigo-600 transition-colors"
                                      >
                                        <Pencil className="size-3.5 hover:text-indigo-600" />
                                      </button>
                                    </Tip>
                                    <Tip label="Delete Component" side="bottom">
                                      <button
                                        onClick={() =>
                                          requestDelete(
                                            "Component",
                                            comp.componentUuid,
                                            comp.title,
                                            () =>
                                              handleDeleteComponent(
                                                comp.componentUuid,
                                              ),
                                          )
                                        }
                                        disabled={
                                          deletingComponentId ===
                                          comp.componentUuid
                                        }
                                        className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-slate-500 dark:text-white/70 hover:text-red-600 transition-colors disabled:opacity-50"
                                      >
                                        <Trash2 className="size-3.5 hover:text-red-600" />
                                      </button>
                                    </Tip>
                                  </div>
                                )}
                              </div>
                              {comp.description && (
                                <p className="text-[11px] text-slate-600 dark:text-white/60 leading-relaxed line-clamp-2 mt-2">
                                  {comp.description}
                                </p>
                              )}
                              <div className="flex items-center gap-4 mt-2 pt-2 border-t border-slate-200 dark:border-white/[0.08]">
                                <button
                                  onClick={() =>
                                    handleOpenComponentDetail(comp.id)
                                  }
                                  className="flex items-center gap-1 text-[11px] text-slate-600 dark:text-white/60 hover:text-slate-900 dark:hover:text-white transition-colors"
                                >
                                  <span className="font-bold text-slate-900 dark:text-white">
                                    {comp.assetCount ?? 0}
                                  </span>{" "}
                                  Assets
                                </button>
                                <button
                                  onClick={() => {
                                    handleOpenComponentDetail(comp.id);
                                    setCompDetailTab("controls");
                                  }}
                                  className="flex items-center gap-1 text-[11px] text-slate-600 dark:text-white/60 hover:text-slate-900 dark:hover:text-white transition-colors"
                                >
                                  {comp.displayStatus === "CONTROLS_ASSIGNED" ? (
                                    <span>{getControlCountLabel(comp)}</span>
                                  ) : (
                                    <>
                                      <span className="font-bold text-slate-900 dark:text-white">
                                        {comp.implementedControlCount ?? 0}
                                      </span>{" "}
                                      Controls
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card p-5 text-center py-12">
          <Server className="size-8 text-muted-foreground mb-3 mx-auto" />
          <p className="text-sm font-medium">No components defined</p>
          <p className="text-xs text-muted-foreground mt-1">Add components to this SSP</p>
        </div>
      )}
    </>
  );
}
